/**
 * Thin fetch wrapper. Cookie session is sent automatically because we always
 * use `credentials: 'include'` and dev/prod both serve the API under the
 * same origin (Vite proxies `/api` → backend; nginx routes it in prod).
 *
 * Bearer Token is only relevant when bridging into the vr-app — see
 * `/auth/exchange-token`. The web-app itself runs on the cookie.
 */

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API ${status}`)
    this.status = status
    this.body = body
  }
}

export const REQUEST_TIMEOUT_MS = 20_000

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(init?.body && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(init?.headers ?? {}),
      },
    })
    const text = await res.text()
    const body = text ? safeJson(text) : null
    if (!res.ok) {
      const msg =
        body && typeof body === 'object' && 'detail' in body
          ? String((body as { detail: unknown }).detail)
          : res.statusText
      throw new ApiError(res.status, body, msg)
    }
    return body as T
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('La conexión tardó demasiado. Revisá internet y probá de nuevo.')
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s)
  } catch {
    return s
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PATCH', body: data ? JSON.stringify(data) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
